import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import ReportFullViewTableTitle, {
  useReportFullViewSection,
} from '@components/pages/Reports/shared/ReportFullViewTableTitle.component'
import ReportExpandableSection from '@components/pages/Reports/shared/ReportExpandableSection.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  ExamUnscheduledProviderRow,
  TREATMENT_CONVERSION_FUNNEL_FILTERS,
  TREATMENT_CONVERSION_FUNNEL_KPI_DEFINITIONS,
} from '@constants/Reports/treatmentConversionFunnel.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import { useTreatmentConversionFunnel } from '@hooks/reports/useTreatmentConversionFunnel'
import { formatCount } from '@utils/dashboardFinance.util'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  formatKpiCurrency,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_LINE_CHART_MARGIN = { top: 8, right: 12, left: 4, bottom: 2 }
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 46

function computeCountAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax = max <= 0 ? 10 : Math.ceil((max * 1.15) / 10) * 10 || 10
  const step = axisMax <= 20 ? 5 : axisMax <= 50 ? 10 : Math.ceil(axisMax / 4)
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  return { domain: [0, axisMax], ticks }
}

function computePercentAxis(values: number[]): {
  domain: [number, number]
  ticks: number[]
} {
  const max = values.length ? Math.max(...values, 0) : 0
  const axisMax =
    max <= 0 ? 100 : Math.min(100, Math.ceil((max * 1.15) / 5) * 5)
  const step = axisMax <= 25 ? 5 : axisMax <= 50 ? 10 : 20
  const ticks: number[] = []
  for (let t = 0; t <= axisMax; t += step) ticks.push(t)
  return { domain: [0, axisMax], ticks }
}

function feesToK(value: number): number {
  return Math.round((value / 1000) * 10) / 10
}

function TreatmentConversionFunnelReport(): React.ReactElement {
  const [funnelView, setFunnelView] = useState<ReportsDistributionView>('Count')
  const [bucketView, setBucketView] = useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const providerTableFullView = useReportFullViewSection('tcf-provider')
  const { loading, error, data } = useTreatmentConversionFunnel(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load treatment conversion funnel data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return TREATMENT_CONVERSION_FUNNEL_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    return TREATMENT_CONVERSION_FUNNEL_KPI_DEFINITIONS.map((def) => {
      let value = '—'
      let footer = def.footer
      if (def.id === 'treatment-acceptance-rate') {
        value = formatKpiPercent(data.treatmentAcceptanceRate.value)
      } else if (def.id === 'unscheduled-treatment-value') {
        value = formatKpiCurrency(data.unscheduledTreatmentValue.value)
        footer = `${formatCount(data.unscheduledTreatmentValue.patientCount)} patients`
      } else if (def.id === 'exam-unscheduled-cases') {
        value = formatCount(data.examUnscheduledCases.value)
      } else if (def.id === 'completed-vs-diagnosed') {
        value = formatKpiPercent(data.completedVsDiagnosed.value)
      }
      return { id: def.id, title: def.title, footer, value }
    })
  }, [data, loading])

  const funnelStageRows = useMemo(() => {
    if (!data) return []
    return data.conversionFunnel.stages.map((stage) => ({
      id: stage.id,
      label: stage.label,
      count: stage.count,
      sumFeesK: feesToK(stage.value),
    }))
  }, [data])

  const funnelFirstCount = funnelStageRows[0]?.count || 1

  const funnelChartData = useMemo(
    () =>
      funnelStageRows.map((row) => {
        const pctCount = Math.round((row.count / funnelFirstCount) * 1000) / 10
        const barValue = funnelView === 'Count' ? row.count : pctCount
        const topLabel =
          funnelView === 'Count' ? String(row.count) : `${pctCount}%`
        return { ...row, barValue, topLabel, pctCount }
      }),
    [funnelView, funnelFirstCount, funnelStageRows],
  )

  const funnelYAxis = useMemo(() => {
    if (funnelView === 'Count') {
      return {
        ...computeCountAxis(funnelStageRows.map((r) => r.count)),
        tickFormatter: (v: number) => String(v),
      }
    }
    return {
      domain: [0, 100] as [number, number],
      ticks: [0, 25, 50, 75, 100],
      tickFormatter: (v: number) => `${v}%`,
    }
  }, [funnelView, funnelStageRows])

  const valueBucketRows = useMemo(() => {
    if (!data) return []
    return data.unscheduledOpportunities.buckets.map((bucket) => ({
      id: bucket.id,
      label: bucket.label,
      count: bucket.count,
      feesK: feesToK(bucket.value),
    }))
  }, [data])

  const bucketTotalPatients = useMemo(
    () => valueBucketRows.reduce((sum, r) => sum + r.count, 0) || 1,
    [valueBucketRows],
  )

  const bucketChartData = useMemo(
    () =>
      valueBucketRows.map((row) => {
        const pct = Math.round((row.count / bucketTotalPatients) * 1000) / 10
        const barValue = bucketView === 'Count' ? row.count : pct
        const topLabel = bucketView === 'Count' ? String(row.count) : `${pct}%`
        return { ...row, barValue, topLabel, pct }
      }),
    [bucketView, bucketTotalPatients, valueBucketRows],
  )

  const bucketYAxis = useMemo(() => {
    if (bucketView === 'Count') {
      return {
        ...computeCountAxis(valueBucketRows.map((r) => r.count)),
        tickFormatter: (v: number) => String(v),
      }
    }
    const values = valueBucketRows.map(
      (row) => Math.round((row.count / bucketTotalPatients) * 1000) / 10,
    )
    return {
      ...computePercentAxis(values),
      tickFormatter: (v: number) => `${v}%`,
    }
  }, [bucketView, bucketTotalPatients, valueBucketRows])

  const acceptanceTrendData = useMemo(() => {
    if (!data) return []
    return data.acceptanceRateTrend.buckets.map((row) => ({
      month: row.label,
      rate: row.value,
    }))
  }, [data])

  const acceptanceYAxis = useMemo(() => {
    const values = acceptanceTrendData.map((row) => row.rate)
    return computePercentAxis(values)
  }, [acceptanceTrendData])

  const providerRows = useMemo((): ExamUnscheduledProviderRow[] => {
    if (!data) return []
    return data.examUnscheduledByProvider.rows.map((row) => ({
      id: row.providerId,
      provider: row.provider,
      examDiagnoses: row.examDiagnoses,
      unscheduledCount: row.unscheduledCount,
      unscheduledPct: row.unscheduledPct,
    }))
  }, [data])

  const providerColumns: ReportDataTableColumn<ExamUnscheduledProviderRow>[] =
    useMemo(() => {
      const cardColumns: ReportDataTableColumn<ExamUnscheduledProviderRow>[] = [
        { key: 'provider', label: 'Provider' },
        { key: 'examDiagnoses', label: 'Exam Diagnoses' },
      ]
      if (!providerTableFullView.isFullView) return cardColumns
      return [
        ...cardColumns,
        { key: 'unscheduledCount', label: 'Unscheduled (count)' },
        {
          key: 'unscheduledPct',
          label: 'Unscheduled (%)',
          render: (row) => `${row.unscheduledPct}%`,
        },
      ]
    }, [providerTableFullView.isFullView])

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.treatmentAcceptanceRate.value,
      data?.unscheduledTreatmentValue.value,
      data?.unscheduledTreatmentValue.patientCount,
      data?.examUnscheduledCases.value,
      data?.completedVsDiagnosed.value,
    ),
  )
  const funnelEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.conversionFunnel?.stages ?? []).some((row) =>
      hasNonZeroDashboardValues(row.count, row.value),
    ),
  )
  const bucketEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.unscheduledOpportunities?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.count, row.value),
    ),
  )
  const acceptanceEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.acceptanceRateTrend?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const providerEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.examUnscheduledByProvider?.rows ?? []).some((row) =>
      hasNonZeroDashboardValues(
        row.examDiagnoses,
        row.unscheduledCount,
        row.unscheduledPct,
      ),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Treatment Conversion Funnel">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={TREATMENT_CONVERSION_FUNNEL_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      <div className="reports-treatment-conversion-funnel__stack">
        <ReportExpandableSection sectionId="tcf-upper">
          {kpiEmpty ? (
            <DashboardSectionEmptyState />
          ) : (
            <ReportKpiGrid items={kpiItems} />
          )}

          <div className="reports-treatment-conversion-funnel__grid">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Conversion Funnel (Value)
                  </ReportSectionHeading>
                  {funnelEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={funnelView}
                      onChange={(value) =>
                        setFunnelView(value as ReportsDistributionView)
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                <p className="reports-treatment-conversion-funnel__chart-note">
                  Diagnosed → Scheduled → Completed (sum of fees)
                </p>
                {funnelEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-treatment-conversion-funnel__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={funnelChartData}
                        margin={REPORT_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={funnelYAxis.domain}
                          ticks={funnelYAxis.ticks}
                          tickFormatter={funnelYAxis.tickFormatter}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH}
                        />
                        <Tooltip
                          formatter={(
                            value: number,
                            _name: string,
                            item: {
                              payload?: { count?: number; sumFeesK?: number }
                            },
                          ) => {
                            const p = item.payload
                            if (funnelView === 'Count')
                              return [p?.count ?? value, 'Patients']
                            return [`${value}%`, 'Of diagnosed (count)']
                          }}
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Bar
                          dataKey="barValue"
                          radius={[8, 8, 8, 8]}
                          fill="var(--blue-400)"
                          barSize={100}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>

            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Unscheduled Treatment Opportunities (Value Buckets)
                  </ReportSectionHeading>
                  {bucketEmpty ? null : (
                    <ReportViewTabs
                      options={REPORTS_DISTRIBUTION_VIEW_TABS}
                      value={bucketView}
                      onChange={(value) =>
                        setBucketView(value as ReportsDistributionView)
                      }
                      className="reports-patient-base-health__view-group"
                    />
                  )}
                </div>
                <p className="reports-treatment-conversion-funnel__chart-note">
                  Patients by unscheduled value bucket
                </p>
                {bucketEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-treatment-conversion-funnel__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={bucketChartData}
                        margin={REPORT_BAR_CHART_MARGIN}
                        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="label"
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={bucketYAxis.domain}
                          ticks={bucketYAxis.ticks}
                          tickFormatter={bucketYAxis.tickFormatter}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH}
                        />
                        <Tooltip
                          formatter={(
                            value: number,
                            _name: string,
                            item: { payload?: { feesK?: number } },
                          ) => {
                            const fees = item.payload?.feesK
                            if (bucketView === 'Count')
                              return [
                                fees != null
                                  ? `${value} patients · $${fees}K`
                                  : String(value),
                                'Bucket',
                              ]
                            return [`${value}%`, 'Share']
                          }}
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Bar
                          dataKey="barValue"
                          radius={[8, 8, 8, 8]}
                          fill="var(--blue-400)"
                          barSize={100}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </div>
        </ReportExpandableSection>

        <div className="reports-treatment-conversion-funnel__grid">
          <ReportExpandableSection sectionId="tcf-accept">
            <ReportSectionCard className="reports-patient-base-health__chart-card">
              <div className="reports-patient-value-revenue__column">
                <div className="reports-patient-base-health__chart-head">
                  <ReportSectionHeading>
                    Treatment Acceptance Rate (Trend)
                  </ReportSectionHeading>
                </div>
                <p className="reports-treatment-conversion-funnel__chart-note">
                  Accepted plans / diagnosed plans
                </p>
                {acceptanceEmpty ? (
                  <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
                ) : (
                  <div className="reports-treatment-conversion-funnel__chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={acceptanceTrendData}
                        margin={REPORT_LINE_CHART_MARGIN}
                      >
                        <CartesianGrid
                          vertical={false}
                          strokeDasharray="0"
                          stroke={REPORT_CHART_GRID_STROKE}
                        />
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                        />
                        <YAxis
                          domain={acceptanceYAxis.domain}
                          ticks={acceptanceYAxis.ticks}
                          tickFormatter={(v) => `${v}%`}
                          axisLine={false}
                          tickLine={false}
                          tick={REPORT_CHART_AXIS_TICK}
                          width={REPORT_YAXIS_WIDTH}
                        />
                        <Tooltip
                          formatter={(value: number) => [`${value}%`, 'Rate']}
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Line
                          type="linear"
                          dataKey="rate"
                          name="Acceptance"
                          stroke="var(--blue-500)"
                          strokeWidth={2}
                          dot={false}
                          activeDot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </ReportSectionCard>
          </ReportExpandableSection>

          <ReportExpandableSection sectionId="tcf-provider">
            <ReportSectionCard className="reports-patient-base-health__chart-card reports-treatment-conversion-funnel__table-card">
              <div className="reports-patient-base-health__chart-head">
                <ReportFullViewTableTitle
                  sectionId="tcf-provider"
                  title="Exam-based Unscheduled Treatment (by Provider)"
                  disableEnter={providerEmpty}
                />
              </div>
              <p className="reports-treatment-conversion-funnel__chart-note">
                Diagnosed during exams but not scheduled
              </p>
              {providerEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <ReportDataTable
                  key={
                    providerTableFullView.isFullView
                      ? 'tcf-provider-fv'
                      : 'tcf-provider-card'
                  }
                  columns={providerColumns}
                  rows={providerRows}
                  rowKey={(row) => row.id}
                  initialPageSize={5}
                  className={[
                    'reports-treatment-conversion-funnel__provider-table',
                    providerTableFullView.isFullView
                      ? 'reports-patient-value-revenue__provider-table--full-view'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  fullViewSectionId="tcf-provider"
                  fullViewSearchWithFilter
                />
              )}
            </ReportSectionCard>
          </ReportExpandableSection>
        </div>
      </div>
    </div>
  )
}

export default TreatmentConversionFunnelReport
